<script setup lang="ts">

import { useGameConfigStore } from '@/stores/gameConfig';
import FlipCoinDesc from '@/views/Game/src/componennts/FlipCoinDesc.vue';
import FlipCoinHistoryTable from './FlipCoinHistoryTable.vue';
import { QNotify } from '@/utils/QNotify';
import * as FlipCoinApi from '@/api/finance/game/flipCoin';
import { fenToYuan, processPriceUnit, yuanToFen } from '@/utils/price';
import { useUserStore } from '@/stores/user';

defineOptions({
  name: 'FlipCoin'
});

const FlipCoinType = FlipCoinApi.FlipCoinType;

const { updateBalance } = useUserStore();

const coin = ref(0);
const animationBox = ref();
const coinContainer = ref();

const flipping = ref(false);

const { getFlipCoinConfig } = toRefs(useGameConfigStore());
const { updateFlipCoinConfig } = useGameConfigStore();

onMounted(() => {
  updateFlipCoinConfig();
});

// 金额
const minPrice = computed(() => {
  return fenToYuan(getFlipCoinConfig.value.minPrice || 1000);
});
const price = ref(minPrice.value);
const priceRule = [
  val => !!val || 'Bet amount is Required',
  val => val >= minPrice.value || 'Bet amount must be greater than ' + minPrice.value
];

const addPrice = () => {
  price.value = Number(price.value) * 2;
};

const minusPrice = () => {
  price.value = Number(price.value) / 2;
  checkPrice();
};

const checkPrice = () => {
  if (price.value < minPrice.value) {
    price.value = minPrice.value;
  }
};

watch(
  () => minPrice.value,
  () => {
    checkPrice();
  }
);

// 金额
const reward = ref(0);
// 翻转结果
const gameResult = ref(FlipCoinType.FRONT.value);

const showRules = ref(false);

const showHistory = ref(false);

const showNotify = ref(false);

const flipCoin = async (type: number) => {
  // 防止1.5秒内重复点击
  if (timer) return;
  flipping.value = true;

  // 更新配置 防止用户页面未刷新导致显示出错
  updateFlipCoinConfig();

  // 开始旋转
  handleRotate();

  // 延迟1s请求，在转的过程中改变显示的图片
  setTimeout(() => {
    FlipCoinApi.play({
      userResult: type,
      price: yuanToFen(price.value)
    }).then(res => {
      gameResult.value = res.gameResult;
      reward.value = fenToYuan(res.reward);
      showNotify.value = true;
      coin.value = gameResult.value === FlipCoinType.FRONT.value ? 0 : 1;
    }).catch(err => {
      showNotify.value = false;
      updateBalance();
    });
  }, 1000);
};

let timer;
const handleRotate = () => {
  animationBox.value.classList.add('coin_animation');
  timer = setTimeout(() => {
    animationBox.value.classList.remove('coin_animation');

    if (showNotify.value) {
      if (reward.value > 0) {
        QNotify.custom({
          type: 'positive',
          message: 'Congrats! you win ' + processPriceUnit(reward.value),
          timeout: 3000
        });
        reward.value = 0;
      } else {
        QNotify.info('Sorry, you failed.');
      }
    }

    timer = null;
    flipping.value = false;
    updateBalance();
  }, 3000);
};

const isDark = import.meta.env.VITE_APP_DARK_MODE === 'true';

</script>

<template>
  <div class="col-grow column justify-center">
    <div class="col column justify-between flip-coin relative-position">
      <!--      <div class="col-2"></div>-->
      <!-- 输入金额 -->
      <div class="col-2 q-px-md">
        <!--        <div class="q-px-md">-->
        <div class="text-primary text-weight-bold">
          {{ 'Bet Amount' }}
        </div>
        <div class="row">
          <q-input v-model="price" class="col-8"
                   outlined clearable :dark="isDark"
                   :rules="priceRule" type="number"
          >
          </q-input>
          <div class="col-4">
            <div class="row justify-around bg-button text-caption" style="border-radius: 5px">
              <q-btn class="q-py-xs bg-transparent" label="1/2" :ripple="false"
                     flat noCaps :disable="flipping || price <= minPrice"
                     @click="minusPrice"
              />
              <q-separator inset vertical />
              <q-btn class="q-py-md bg-transparent" label="2x" :ripple="false"
                     flat noCaps :disable="flipping"
                     @click="addPrice"
              />
            </div>
          </div>
        </div>
        <!--        </div>-->
      </div>
      <!-- 金币-->
      <div class="col-grow column justify-center relative-position" ref="animationBox">
        <q-badge floating rounded color="transparent"
                 style="right: 10px; top: 5px"
                 @click="showRules = true"
        >
          <q-icon name="info" size="1.5rem" color="primary" />
        </q-badge>
        <div class="coin_container q-mx-auto"
             ref="coinContainer">
          <!--             @click="handleRotate"-->
          <div :class="coin ? 'coin_back' : 'coin_front'">
            <div class="c1"></div>
            <div class="c2"></div>
            <div class="c3"></div>
            <div class="c4"></div>
            <div class="image">
              <img class="head_image" src="@/assets/game/flipCoin/head.png" alt="">
            </div>
            <div class="head_text text text_upper">HEAD</div>
          </div>
          <div :class="coin ? 'coin_front' : 'coin_back'">
            <div class="c1"></div>
            <div class="c2"></div>
            <div class="c3"></div>
            <div class="c4"></div>
            <div class="image">
              <img class="tail_image" src="@/assets/game/flipCoin/tail.png" alt="">
            </div>
            <div class="tail_text text text_upper">TAIL</div>
          </div>
        </div>
      </div>
      <div class="col-3 q-pa-md q-pb-lg row justify-around guess-buttons">
        <q-btn class="q-py-sm bg-button text-h6 radius-sm guess-button"
               :label="FlipCoinType.FRONT.name"
               rounded noCaps unelevated :disable="flipping"
               @click="flipCoin(FlipCoinType.FRONT.value)"
        />
        <!--               @click="flipStart"-->
        <q-btn class="q-py-sm bg-button text-h6 radius-sm guess-button"
               :label="FlipCoinType.BACK.name"
               rounded noCaps unelevated :disable="flipping"
               @click="flipCoin(FlipCoinType.BACK.value)"
        />
        <!--               @click="flipStop"-->
      </div>
      <div class="col-2 q-pa-md column">
        <q-btn class="q-py-sm bg-button text-h6 radius-sm guess-button"
               label="My Stats"
               rounded noCaps unelevated :disable="flipping"
               @click="showHistory = true"
        />
      </div>
    </div>
    <q-dialog v-model="showRules" position="top" full-width>
      <FlipCoinDesc />
    </q-dialog>
    <q-dialog v-model="showHistory" position="bottom" full-width>
      <FlipCoinHistoryTable />
    </q-dialog>
  </div>
</template>

<style lang="scss" scoped>


.coin_container {

  $size: 300px;

  width: $size;
  height: $size;
  //cursor: pointer;
  border-radius: 50%;
  transform-style: preserve-3d;
  position: relative;
  top: 0;
  transition: transform 3s ease-in-out;
  -moz-backface-visibility: hidden;

  .coin_front,
  .coin_back {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    border-radius: 50%;
  }

  .coin_back {
    transform: rotateX(180deg);
  }

  .coin_front div,
  .coin_back div {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    border-radius: 50%;
    overflow: hidden;
  }

  .coin_front div:nth-of-type(1),
  .coin_back div:nth-of-type(1) {
    //background: linear-gradient(138.4deg, #4f0bb2 16%, #f71183 114%);
    z-index: 1;
    top: 0%;
    left: 0%;
  }


  .coin_front div:nth-of-type(2),
  .coin_back div:nth-of-type(2) {
    //background: linear-gradient(338deg, #7844c5 16%, #d975a6 114%);
    z-index: 2;
    width: 98%;
    height: 98%;
    top: 1%;
    left: 1%;
  }

  .coin_front div:nth-of-type(3),
  .coin_back div:nth-of-type(3) {
    background: linear-gradient(121.59deg, #ffe695 60%, #ffffff 100%);
    z-index: 3;
    width: 90%;
    height: 90%;
    top: 5%;
    left: 5%;
  }

  .coin_front div:nth-of-type(4),
  .coin_back div:nth-of-type(4) {
    //background: linear-gradient(326deg, #b684ff 16%, #fdbcdc 114%);
    z-index: 4;
    width: 84%;
    height: 84%;
    top: 8%;
    left: 8%;
  }

  .coin_front div:nth-of-type(5),
  .coin_back div:nth-of-type(5) {
    z-index: 5;
    width: 84%;
    height: 84%;
    top: 8%;
    left: 8%;
    background-color: transparent;
    border-radius: 50%;
  }

  .coin_front .image,
  .coin_back .image {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .coin_front .image img,
  .coin_back .image img {
    height: 100%;
    user-select: none;
  }

  .coin_front div:nth-of-type(6),
  .coin_back div:nth-of-type(6) {
    width: 120px;
    height: 50px;
    z-index: 6;
    font-size: 30px;
    font-weight: 800;
    top: 70%;
    left: 30%;
    color: rgba(255, 255, 255, 0.5);
    //text-shadow: -5px 4px 15px rgba(0, 0, 0, 0.25);
    border-radius: 0px;
    text-align: center;
    text-transform: uppercase;
    user-select: none;
  }


}

@keyframes container-animation {
  0% {
    transform: scale(1) rotateX(0deg);
    animation-timing-function: ease-in;
  }
  50% {
    transform: scale(1.3) rotateX(1890deg);
    animation-timing-function: ease-out;
  }
  //75% {
  //  transform: scale(1) rotateX(2700deg)
  //}
  100% {
    transform: scale(1) rotateX(3600deg)
  }
}

.coin_animation {
  .coin_container {
    animation: container-animation 3s ease-in 0s infinite;
  }
}

.guess-buttons {
  .guess-button {
    width: 140px;
  }
}

</style>